// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Manage projects (design/agents-project-picker f-manage, f-remove): one machine's projects
// with their chats and last use; rename, copy the path, or remove from the agent tool. Removing
// says what stays (the folder and its files) and what goes (the project's chats in the tool).

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {MachineGlyph, OnlineDot} from '@app/features/konvo_agents/components/AgentBrand';
import styles from '@app/features/konvo_agents/components/AgentProjectPicker.module.css';
import {agentProblemMessage, projectLastUsed} from '@app/features/konvo_agents/components/AgentsCopy';
import {homeOf, removeProject, renameProject} from '@app/features/konvo_agents/state/AgentProjects';
import {projectUse, sortProjects, tildePath} from '@app/features/konvo_agents/state/AgentProjectsModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {AgentMachineView, AgentProject} from '@konvo/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconAlertTriangle,
	IconCopy,
	IconFolder,
	IconPencil,
	IconPlus,
	IconSearch,
	IconTrash,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

export const AgentProjectManage = observer(
	({machine, layout, onAdd}: {machine: AgentMachineView; layout: 'panes' | 'chips'; onAdd: () => void}) => {
		const {t, i18n} = useLingui();
		const [query, setQuery] = useState('');
		const [renaming, setRenaming] = useState<{id: string; title: string} | null>(null);
		const [saving, setSaving] = useState(false);
		const now = Agents.minute;
		const uses = projectUse(Agents.threadsOf(machine.id), now);
		const home = homeOf(machine.id);
		const tool = machine.tools[0]?.name ?? 'T3 Code';
		const machineName = machine.name;
		const projectCount = Agents.projectsOf(machine.id).length;
		const needle = query.trim().toLocaleLowerCase();
		const projects = sortProjects(Agents.projectsOf(machine.id), uses).filter(
			(p) => !needle || `${p.title} ${p.path ?? ''}`.toLocaleLowerCase().includes(needle),
		);

		const saveRename = async () => {
			if (!renaming) return;
			const title = renaming.title.trim();
			const before = Agents.projectsOf(machine.id).find((p) => p.id === renaming.id)?.title;
			if (!title || title === before) return setRenaming(null);
			setSaving(true);
			try {
				await renameProject(machine.id, renaming.id, title);
				setRenaming(null);
			} catch (error) {
				ToastCommands.error(agentProblemMessage(i18n, error));
			} finally {
				setSaving(false);
			}
		};

		const confirmRemove = (project: AgentProject) => {
			const chats = uses.get(project.id)?.chats ?? 0;
			const title = project.title;
			const path = tildePath(project.path, home) || title;
			ModalCommands.push(
				ModalCommands.modal(() => (
					<ConfirmModal
						title={t`Remove ${title} from ${tool}?`}
						description={
							<>
								<Trans>
									The folder <strong>{path}</strong> stays on {machineName} with all its files. Only the project in{' '}
									{tool} goes.
								</Trans>
								{chats > 0 && (
									<span className={styles.warn}>
										<IconAlertTriangle size={16} stroke={1.8} />
										<span>
											{plural(chats, {
												one: 'Its # chat is deleted from the agent tool too. This can’t be undone.',
												other: 'Its # chats are deleted from the agent tool too. This can’t be undone.',
											})}
										</span>
									</span>
								)}
							</>
						}
						primaryText={
							chats > 0
								? plural(chats, {one: 'Remove project and # chat', other: 'Remove project and # chats'})
								: t`Remove project`
						}
						primaryVariant="danger"
						onPrimary={async () => {
							try {
								await removeProject(machine.id, project.id);
								ToastCommands.success(t`Removed ${title} from ${machineName}.`);
							} catch (error) {
								ToastCommands.error(agentProblemMessage(i18n, error));
							}
						}}
					/>
				)),
			);
		};

		return (
			<div className={styles.main} data-flx="konvo-agents.manage-projects">
				<div className={styles.pad}>
					<label className={styles.search}>
						<IconSearch size={17} stroke={1.7} />
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={t`Search projects on ${machineName}`}
							aria-label={t`Search projects on ${machineName}`}
						/>
					</label>
				</div>
				<div className={styles.meta}>
					<MachineGlyph platform={machine.platform} size={14} />
					{`${tool} · ${plural(projectCount, {one: '# project', other: '# projects'})}`}
					<span className={styles.metaEnd}>
						{layout === 'panes' ? (
							<Button small variant="secondary" leftIcon={<IconPlus size={15} stroke={2} />} onClick={onAdd}>
								{t`Add project`}
							</Button>
						) : (
							<>
								<OnlineDot online={machine.online} />
								{machine.online ? t`Online` : t`Offline`}
							</>
						)}
					</span>
				</div>
				<div className={styles.scroll} data-flx="konvo-agents.manage-projects.list">
					{projects.length === 0 && (
						<p className={styles.empty}>{needle ? t`No projects match.` : t`No projects on ${machineName} yet.`}</p>
					)}
					{projects.map((project) => {
						const use = uses.get(project.id);
						const chats = use?.chats ?? 0;
						const path = tildePath(project.path, home);
						const editing = renaming?.id === project.id;
						const projectTitle = project.title;
						return (
							<div key={project.id} className={styles.manageRow} data-flx="konvo-agents.manage-projects.row">
								<IconFolder size={18} stroke={1.6} className={styles.folder} />
								<span className={styles.manageText}>
									{editing ? (
										<input
											className={styles.renameInput}
											value={renaming.title}
											maxLength={200}
											disabled={saving}
											onChange={(event) => setRenaming({id: project.id, title: event.target.value})}
											onBlur={() => void saveRename()}
											onKeyDown={(event) => {
												if (event.key === 'Enter') {
													event.preventDefault();
													void saveRename();
												} else if (event.key === 'Escape') {
													event.stopPropagation();
													setRenaming(null);
												}
											}}
											aria-label={t`Project name`}
											// biome-ignore lint/a11y/noAutofocus: renaming was just asked for.
											autoFocus
											data-flx="konvo-agents.manage-projects.rename-input"
										/>
									) : (
										<strong>{project.title}</strong>
									)}
									<span title={project.path}>{path}</span>
								</span>
								<span className={styles.chats}>
									{chats > 0 ? plural(chats, {one: '# chat', other: '# chats'}) : t`No chats`}
								</span>
								{layout === 'panes' && (
									<span className={styles.when}>{projectLastUsed(i18n, use && {last: use.last, today: 0}, now)}</span>
								)}
								<span className={clsx(styles.acts, layout === 'chips' && styles.actsAlways)}>
									<Tooltip text={t`Rename`} position="top">
										<button
											type="button"
											className={styles.act}
											onClick={() => setRenaming({id: project.id, title: project.title})}
											aria-label={t`Rename ${projectTitle}`}
											data-flx="konvo-agents.manage-projects.rename"
										>
											<IconPencil size={16} stroke={1.7} />
										</button>
									</Tooltip>
									{project.path && (
										<Tooltip text={t`Copy path`} position="top">
											<button
												type="button"
												className={styles.act}
												onClick={() => void TextCopyCommands.copy(i18n, project.path!)}
												aria-label={t`Copy the path of ${projectTitle}`}
											>
												<IconCopy size={16} stroke={1.7} />
											</button>
										</Tooltip>
									)}
									<Tooltip text={t`Remove from ${tool}`} position="top">
										<button
											type="button"
											className={clsx(styles.act, styles.actDanger)}
											onClick={() => confirmRemove(project)}
											aria-label={t`Remove ${projectTitle}`}
											data-flx="konvo-agents.manage-projects.remove"
										>
											<IconTrash size={16} stroke={1.7} />
										</button>
									</Tooltip>
								</span>
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);
